import type { TFunction } from "i18next";
import type { FacilityType } from "../types/facility.definition";
import {
  AcceptedCountries,
  CivilityEnum,
  NotificationPrefType,
  RoleType,
} from "../types/user.definition";

/**
 * MED-1462 / MED-1464 — the admin used to print API enums straight onto the
 * page: CLINIC, HEALTH_CENTRE, MAN, WOMAN, Monday, SENEGAL. These helpers map
 * each enum onto the translation it already has, and fall back to the raw
 * value rather than to a raw translation key, so an enum the API adds
 * tomorrow degrades to something readable instead of
 * "admin.facilities.types.whatever" on screen.
 */
function translateOrRaw(t: TFunction, key: string, raw: string): string {
  const label = t(key);
  return label === key ? raw : label;
}

/** Translation for a facility type, e.g. CLINIC -> "Clinique". */
export function facilityTypeLabel(
  t: TFunction,
  type: FacilityType | string | null | undefined
): string {
  if (!type) return "";
  const raw = String(type);
  return translateOrRaw(t, `admin.facilities.types.${raw.toLowerCase()}`, raw);
}

/**
 * Translation for a civility, e.g. MAN -> "M.". The API enum is MAN/WOMAN;
 * the labels are the ones the manager forms already use.
 */
export function civilityLabel(
  t: TFunction,
  civility: string | null | undefined
): string {
  switch (civility) {
    case CivilityEnum.MR:
      return t("common.mr");
    case CivilityEnum.MRS:
      return t("common.mrs");
    default:
      return civility ?? "";
  }
}

/** Translation for a country, e.g. CHAD -> "Tchad". */
export function countryLabel(
  t: TFunction,
  country: AcceptedCountries | string | null | undefined
): string {
  if (!country) return "";
  const raw = String(country);
  return translateOrRaw(t, `common.countries.${raw}`, raw);
}

/** Translation for an opening day, e.g. "monday" -> "Lundi". */
export function weekdayLabel(
  t: TFunction,
  day: string | null | undefined
): string {
  if (!day) return "";
  return translateOrRaw(t, `common.days.${day.toLowerCase()}`, day);
}

/**
 * Translation for a user role, e.g. SUPER_ADMIN -> "Super administrateur"
 * (MED-1516). `admin.users.roles.*` already carries every role the admin can
 * display — the users list has translated them since MED-1462; the profile
 * page printed the raw enum.
 */
export function roleLabel(
  t: TFunction,
  role: RoleType | string | null | undefined
): string {
  if (!role) return "";
  const raw = String(role);
  return translateOrRaw(t, `admin.users.roles.${raw}`, raw);
}

/**
 * Translation for a preferred notification method, e.g. EMAIL -> "Email"
 * (MED-1516 / MED-1523). The doctor list and the admin profile printed the
 * raw enum.
 */
export function notificationMethodLabel(
  t: TFunction,
  method: NotificationPrefType | string | null | undefined
): string {
  if (!method) return "";
  const raw = String(method);
  return translateOrRaw(t, `common.notificationMethods.${raw}`, raw);
}

/**
 * Translation for a licence enum value — plan type, contract duration,
 * payment billing mode or source (MED-1523).
 *
 * `admin.invoices.values.*` is the platform's single label set for these
 * (BASIC / STANDARD / PREMIUM / MONTHLY / ANNUALLY / ANNUAL_LUMP_SUM). It
 * lives under the invoices namespace because the invoice table needed it
 * first; reuse it rather than duplicating the same five translations under a
 * second namespace.
 */
export function licenceValueLabel(
  t: TFunction,
  value: string | null | undefined
): string {
  if (!value) return "";
  const raw = String(value);
  return translateOrRaw(t, `admin.invoices.values.${raw}`, raw);
}

/**
 * Translation for an app-notice scope value — the environment ("qa" -> "QA")
 * or the country ("tchad" -> "Tchad") an announcement/maintenance rule is
 * scoped to (MED-1516). Same `admin.governance.*` mapping the rule form's
 * dropdowns already use; the list rendered the raw lowercase value.
 */
export function appNoticeScopeLabel(
  t: TFunction,
  kind: "environments" | "countries",
  value: string | null | undefined
): string {
  if (!value) return "";
  const raw = String(value);
  return translateOrRaw(t, `admin.governance.${kind}.${raw}`, raw);
}
